import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Send, MessageCircle, Crown, Sparkles } from "lucide-react";

const WHATSAPP_NUMBER = "12428131775";

const CHAT_STYLES = `
@keyframes chat-pop {
  from { opacity: 0; transform: translateY(14px) scale(0.94); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.chat-pop { animation: chat-pop 0.38s cubic-bezier(0.34, 1.4, 0.64, 1) both; transform-origin: bottom right; }
@keyframes chat-typing-dot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.4; } 30% { transform: translateY(-3px); opacity: 1; } }
.chat-typing-dot { animation: chat-typing-dot 1.2s infinite; }
.chat-typing-dot:nth-child(2) { animation-delay: 0.15s; }
.chat-typing-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes chat-aurora { 0%, 100% { transform: translate(-12%, -8%) scale(1); } 50% { transform: translate(10%, 12%) scale(1.18); } }
.chat-aurora { animation: chat-aurora 9s ease-in-out infinite; }
@keyframes chat-shine { from { transform: translateX(-140%) skewX(-18deg); } to { transform: translateX(260%) skewX(-18deg); } }
.chat-shine { animation: chat-shine 4s ease-in-out infinite; }
@keyframes chat-msg-in { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: none; } }
.chat-msg-in { animation: chat-msg-in 0.45s cubic-bezier(0.34, 1.3, 0.64, 1) both; }
`;

const TIER_CHIP = {
  "Island Guest": "bg-teal-50 text-teal-700 border-teal-200",
  "Silver Explorer": "bg-slate-100 text-slate-600 border-slate-300",
  "Gold Voyager": "bg-amber-50 text-amber-700 border-amber-300",
  "Platinum Islander": "bg-[#1A1A1A] text-amber-200 border-amber-500/40",
};

const GUEST_GREETINGS = [
  { heading: "Thinking of island wheels?", body: "Share your dates and we'll match you with the perfect ride — real humans, replies in minutes." },
  { heading: "Flying out of NAS?", body: "Ask about airport drop-off — leave the car with us at the airport when you fly out." },
  { heading: "Staying a week or more?", body: "Rentals of 7+ days save 10% automatically — ask how much you'd save." },
  { heading: "Travelling with little ones?", body: "Baby seats and GPS units available — tell us what you need and we'll have it ready." },
  { heading: "Not sure which vehicle fits?", body: "Tell us your group size and luggage — we'll point you to the right ride." },
  { heading: "First time in Nassau?", body: "We deliver to hotels, villas and the airport — ask us anything about your trip." },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [greetIdx, setGreetIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [loyalty, setLoyalty] = useState(null);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    let email = "";
    try {
      const saved = JSON.parse(localStorage.getItem("ed_visitor") || "null");
      if (saved?.name) setVisitorName(saved.name.split(" ")[0]);
      if (saved?.email) email = saved.email;
    } catch (e) {}
    setReturning(Number(localStorage.getItem("ed_session_count") || "0") >= 3);
    if (email) {
      api.get("/loyalty/status", { params: { email } }).then(({ data }) => {
        if (data.member) setLoyalty({ tier: data.tier, percent: data.percent, memberNo: data.member_no });
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (open || dismissed) return undefined;
    const t = setTimeout(() => setDismissed(true), 10000);
    return () => clearTimeout(t);
  }, [open, dismissed]);

  const pool = (visitorName
    ? [{
        heading: `Welcome back, ${visitorName}!`,
        body: loyalty && loyalty.percent > 0
          ? `Your ${loyalty.percent}% loyalty discount applies automatically — just say the word.`
          : "Your island ride is a message away — same warm service, every time.",
      }, ...GUEST_GREETINGS]
    : returning
      ? [{ heading: "Back again?", body: "We've noticed you've stopped by a few times — returning visitors get priority booking help. Ask us anything and we'll fast-track your rental." }]
      : GUEST_GREETINGS);

  useEffect(() => {
    setGreetIdx(Math.floor(Math.random() * pool.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorName, loyalty]);

  const identity = visitorName
    ? ` It's ${visitorName}${loyalty?.memberNo ? `, member ${loyalty.memberNo}` : ""}.`
    : "";

  const send = () => {
    const text = encodeURIComponent(
      msg.trim() || `Hi Easy Drive!${identity} I'd like to ask about a rental in Nassau.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
    setMsg("");
    setOpen(false);
  };

  const tierChip = loyalty && loyalty.percent > 0 && (
    <span data-testid="chat-tier-chip" className={`inline-flex items-center gap-1 text-[10px] font-ui font-semibold tracking-wide uppercase border rounded-full px-2.5 py-0.5 ${TIER_CHIP[loyalty.tier] || TIER_CHIP["Island Guest"]}`}>
      <Crown size={9} /> {loyalty.tier} · −{loyalty.percent}% awaits
    </span>
  );

  const heading = visitorName ? `Welcome back, ${visitorName}!` : "Thinking of island wheels?";
  const bubble = visitorName
    ? `Hi again, ${visitorName} — wonderful to see you back. Planning another island escape? Ask us anything: availability, pricing, or airport drop-off.`
    : "A warm island welcome! Planning a trip to Nassau? Tell us your dates and we'll match you with the perfect ride — every answer comes from a real human, usually within minutes.";

  const g = pool[greetIdx % pool.length];

  const quickChips = (prefix) => (
    <>
      <button
        data-testid={`${prefix}-availability`}
        onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Easy Drive!${identity} I'd like to check availability for my trip dates: ___`)}`, "_blank")}
        className="group/chip text-left text-[11px] font-ui font-medium text-[#C71E2E] border border-[#C71E2E]/25 bg-white/70 backdrop-blur rounded-full px-3.5 py-1.5 hover:bg-[#C71E2E] hover:text-white hover:shadow-[0_6px_16px_rgba(199,30,46,0.35)] hover:-translate-y-0.5 transition-all duration-200"
      >
        Check availability for my dates <span className="inline-block transition-transform duration-200 group-hover/chip:translate-x-0.5">→</span>
      </button>
      <button
        data-testid={`${prefix}-airport`}
        onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Easy Drive!${identity} Do you offer airport drop-off at NAS?`)}`, "_blank")}
        className="group/chip text-left text-[11px] font-ui font-medium text-[#C71E2E] border border-[#C71E2E]/25 bg-white/70 backdrop-blur rounded-full px-3.5 py-1.5 hover:bg-[#C71E2E] hover:text-white hover:shadow-[0_6px_16px_rgba(199,30,46,0.35)] hover:-translate-y-0.5 transition-all duration-200"
      >
        Ask about airport drop-off <span className="inline-block transition-transform duration-200 group-hover/chip:translate-x-0.5">→</span>
      </button>
    </>
  );

  return (
    <div className="no-print fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 group">
      <style>{CHAT_STYLES}</style>
      {open && (
        <div data-testid="chat-panel" className="chat-pop w-[22rem] max-w-[calc(100vw-3rem)] rounded-[1.9rem] p-[1.5px] bg-gradient-to-br from-[#C71E2E]/70 via-white/50 to-[#C71E2E]/40 shadow-[0_30px_70px_rgba(26,10,12,0.28)]">
          <div className="rounded-[1.8rem] overflow-hidden bg-white/80 backdrop-blur-2xl">
            <div className="relative overflow-hidden bg-gradient-to-br from-[#D6202F] via-[#B01A28] to-[#6E0C17] text-white px-5 py-4">
              <div className="chat-aurora absolute -top-16 -right-10 w-48 h-48 rounded-full bg-white/15 blur-2xl pointer-events-none" />
              <div className="chat-aurora absolute -bottom-16 -left-12 w-44 h-44 rounded-full bg-[#ff7a86]/25 blur-2xl pointer-events-none" style={{ animationDelay: "-3.5s" }} />
              <div className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent chat-shine pointer-events-none" />
              <div className="relative flex items-center gap-3">
                <div className="relative shrink-0">
                  <img src="/favicon.png" alt="Easy Drive" className="w-11 h-11 rounded-full bg-white object-contain p-1 ring-2 ring-white/50 shadow-[0_6px_18px_rgba(0,0,0,0.3)]" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#B01A28]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg tracking-tight leading-tight flex items-center gap-1.5">
                    Easy Drive Concierge <Sparkles size={13} className="text-amber-300" />
                  </p>
                  <p className="text-[11px] font-ui text-white/85 flex items-center gap-1.5 mt-0.5">
                    <span className="flex gap-0.5">
                      <span className="chat-typing-dot w-1 h-1 rounded-full bg-emerald-300 inline-block" />
                      <span className="chat-typing-dot w-1 h-1 rounded-full bg-emerald-300 inline-block" />
                      <span className="chat-typing-dot w-1 h-1 rounded-full bg-emerald-300 inline-block" />
                    </span>
                    Online — island-fast replies
                  </p>
                </div>
                <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Close chat" className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/15 hover:rotate-90 transition-all duration-300">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="relative p-4 bg-[#FAF6F0]/85 space-y-3 overflow-hidden">
              <div className="chat-aurora pointer-events-none absolute -top-12 -left-12 w-40 h-40 rounded-full bg-[#C71E2E]/10 blur-2xl" />
              <div className="chat-aurora pointer-events-none absolute -bottom-14 -right-10 w-44 h-44 rounded-full bg-amber-400/15 blur-2xl" style={{ animationDelay: "-4.5s" }} />
              <div className="relative flex items-end gap-2">
                <img src="/favicon.png" alt="" className="w-7 h-7 rounded-full bg-white object-contain p-0.5 border border-[#E5DED5] shrink-0 shadow-sm" />
                <div className="max-w-[85%] space-y-1.5">
                  <div className="chat-msg-in bg-white border border-[#E5DED5] rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed shadow-[0_6px_18px_rgba(0,0,0,0.06)]">
                    {bubble}
                  </div>
                  {tierChip}
                </div>
              </div>
              <div className="relative flex flex-col gap-1.5 pt-1">
                {quickChips("chat-panel-quick")}
              </div>
            </div>
            <div className="p-3 border-t border-[#E5DED5] flex items-center gap-2 bg-white/80 backdrop-blur">
              <input
                data-testid="chat-message-input"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask us anything…"
                className="flex-1 h-11 rounded-full border border-[#E5DED5] bg-white px-4 text-sm outline-none focus:border-[#C71E2E]/50 focus:shadow-[0_0_0_4px_rgba(199,30,46,0.12)] transition-all duration-200"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                aria-label="Send message"
                className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D6202F] to-[#8f1220] text-white flex items-center justify-center shrink-0 transition-all duration-300 hover:shadow-[0_0_24px_rgba(199,30,46,0.6)] hover:scale-110 hover:rotate-6 active:scale-95"
              >
                <Send size={15} className="-translate-x-px translate-y-px" />
              </button>
            </div>
            <p className="px-4 pb-3 pt-1.5 text-[10px] font-ui text-[#4A4A4A]/70 bg-white/80 flex items-center gap-1.5">
              <MessageCircle size={10} className="text-[#C71E2E]" />
              Opens WhatsApp — a real human continues the conversation
            </p>
          </div>
        </div>
      )}
      {!open && (
        <div
          data-testid="chat-greeting"
          className={`transition-opacity duration-300 rounded-[1.35rem] p-[1.5px] bg-gradient-to-br from-[#C71E2E]/40 via-white/25 to-[#C71E2E]/20 shadow-[0_18px_48px_rgba(26,10,12,0.14)] max-w-[280px] ${dismissed ? "hidden" : returning ? "opacity-100" : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"}`}
        >
          <div className="relative rounded-[1.3rem] bg-white/60 backdrop-blur-xl px-4 py-3.5 overflow-hidden">
            <div className="chat-aurora pointer-events-none absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#C71E2E]/10 blur-xl" />
            <button
              data-testid="chat-greeting-dismiss"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss greeting"
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground/70 hover:text-foreground hover:bg-black/5 transition-colors duration-200 z-10"
            >
              <X size={12} />
            </button>
            <div className="relative flex items-center gap-2 pr-6">
              <img src="/favicon.png" alt="" className="w-5 h-5 rounded-full bg-white object-contain border border-[#E5DED5]" />
              <p data-testid="chat-greeting-heading" className="text-xs font-ui font-semibold text-foreground">{g.heading}</p>
            </div>
            {loyalty && loyalty.percent > 0 && <div className="relative mt-2">{tierChip}</div>}
            <p data-testid="chat-greeting-body" className="relative text-[11px] font-ui text-muted-foreground mt-1.5 leading-relaxed">
              {g.body}
            </p>
            <div className="relative mt-3 flex flex-col gap-1.5">
              {quickChips("chat-quick")}
            </div>
          </div>
        </div>
      )}
      <button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        aria-label="Chat with us"
        className="chat-fab relative w-[60px] h-[60px] rounded-full flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity duration-300"
      >
        <span data-testid="chat-online-dot" className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#140A0B] z-10" />
        {open ? (
          <X size={20} className="text-white" />
        ) : (
          <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
          </svg>
        )}
      </button>
    </div>
  );
}
